/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import MainLayout from '../../../layouts/MainLayout'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import '../../../assets/styles/custom-styles.css'
import useNotification from '../../../hooks/useNotification'
import useLoader from '../../../hooks/useLoader'
import useForm from '../../../hooks/useForm'
import Form from '../../../components/forms/Form'
import DocumentTypeEnums from '../../../enums/document-enum'
import {
  cleanPayload,
  convertStringsToFloats,
  getMissingRequiredFields,
  normalizeFormErrors,
  setDefaultFieldValues,
} from '../../../utils/objectUtils'
import useBaseService from '../../../hooks/services/useBaseService'
import { entities } from '../../../constants/entities'
import PageHeader from '../../../components/PageHeader'
import SimpleBody from '../../../components/forms/SimpleBody'
import SectionHeader from '../../../components/forms/SectionHeader'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import useSelectOptions from '../../../hooks/useSelectOptions'
import ItemListSection from '../../../components/forms/ItemListSection'
import { v4 as uuidv4 } from 'uuid'
import { useTransactionPageLogic } from './hooks/useTransactionPageLogic'
import useRemoteSelectOptions from '../../../hooks/useRemoteSelectOptions'
import { transformFiltersForUrl } from '../../../utils/transformFiltersForUrl'
import { Box } from '@mui/material'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'
import TransactionEnums from '../../../enums/transaction-enum'
import { transactionFields } from '../../../constants/forms/almoxarifado/transactionFields'
import { useOrgan } from '../../../hooks/useOrgan'
import { forEach } from 'lodash'
import { formatDate } from '../../../utils/formatDate'

const EditTransactionPage = () => {
  const { id } = useParams()
  const { selectedOrgan } = useOrgan()
  const location = useLocation()

  const queryParams = new URLSearchParams(location.search)
  const type = queryParams.get('type')

  const navigate = useNavigate()
  const { showNotification } = useNotification()
  const {
    get: baseServiceGet,
    getByColumn: baseServiceGetByColumn,
    put: baseServicePut,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate)
  const { showLoader, hideLoader } = useLoader()
  const queryClient = useQueryClient()
  const [showEquipmentsSection, setShowEquipmentsSection] = useState(false)
  const [showProductsSection, setShowProductsSection] = useState(false)

  const [transactionSection, productsSection, equipmentsSection] =
    transactionFields

  const { formData, handleChange, setFormData, resetForm } = useForm(
    setDefaultFieldValues([transactionSection]),
    [transactionSection]
  )

  useEffect(() => {
    if (!type) return

    const transactionTypeMap = {
      entrada: { value: TransactionEnums.ENTRADA, label: 'ENTRADA' },
      transferencia: {
        value: TransactionEnums.TRANSFERENCIA,
        label: 'TRANSFERÊNCIA',
      },
    }

    const transactionType = transactionTypeMap[type.toLowerCase()]

    if (transactionType) {
      setFormData((prev) => ({
        ...prev,
        transaction: {
          ...prev.transaction,
          transaction_type_id: transactionType,
        },
      }))
    }
  }, [type])

  const {
    formData: itemFormData,
    handleChange: handleItemChange,
    setFormData: setItemFormData,
    resetForm: resetItemForm,
  } = useForm(setDefaultFieldValues([productsSection]), [productsSection])
  const {
    formData: equipmentFormData,
    handleChange: handleEquipmentChange,
    setFormData: setEquipmentFormData,
    resetForm: resetEquipmentForm,
  } = useForm(setDefaultFieldValues([equipmentsSection]), [equipmentsSection])

  const { data: transactionData, isLoading: isLoadingTransactionData } =
    useQueryWithLoader({
      queryKey: ['almox_transaction', id],
      queryFn: async () => {
        const response = await baseServiceGetByColumn(
          entities.transactions.getById(id)
        )
        return response.result
      },
      enabled: !!id,
      select: (data) => {
        return {
          transaction_type_id: data.type
            ? { value: data.type.id, label: data.type.name }
            : null,
          transaction_date: data.transaction_date
            ? formatDate({ rawDate: data.transaction_date, showTime: false })
            : '',
          doc_type_id: data.document_type
            ? { value: data.document_type.id, label: data.document_type.name }
            : null,
          supplier_id: data.supplier
            ? { value: data.supplier.id, label: data.supplier.name }
            : null,
          doc_number: data.doc_number || '',
          amount_total: data.amount_total || 0,
          amount_discount: data.amount_discount || 0,
          doc_date: data.doc_date
            ? formatDate({ rawDate: data.doc_date, showTime: false })
            : '',
          purchase_date: data.purchase_date
            ? formatDate({ rawDate: data.purchase_date, showTime: false })
            : '',
          sender_id: data.sender
            ? { value: data.sender.id, label: data.sender.name }
            : null,
          receiver_id: data.receiver
            ? { value: data.receiver.id, label: data.receiver.name }
            : null,
          customer_id: data.customer
            ? { value: data.customer.id, label: data.customer.name }
            : null,
          buyer_id: data.buyer
            ? { value: data.buyer.id, label: data.buyer.name }
            : null,
          // ⬅️ CORRIGIDO: campo de endereço de origem
          sender_address_id: data.sender_address
            ? {
                value: data.sender_address.id,
                label: data.sender_address.formatted_address,
              }
            : null,
          user_id: data.user
            ? { value: data.user.id, label: data.user.name }
            : null,
          details: data.details || '',
        }
      },
    })

  const { data: itemsData, isLoading: isLoadingItemsData } = useQueryWithLoader(
    {
      queryKey: ['almox_transaction_items', id],
      queryFn: async () => {
        const response = await baseServiceGet(entities.transactions.items(id))
        return response.result.data
      },
      enabled: !!id,
      select: (data) => {
        if (!data) return { items: [], equipments: [] }

        const items = data.filter((item) => item.product && !item.equipment)
        const equipments = data.filter((item) => item.equipment && item.product)

        return {
          ...(items.length > 0 && {
            items: items.map((item) => {
              const product = item.product
              const matchedProductItem = product?.items?.find(
                (productItem) =>
                  productItem.category_id === item.category_id &&
                  productItem.condition_id === item.condition_id &&
                  (productItem.organization_id ===
                    transactionData?.sender_id?.value || // ⬅️ Adicionado optional chaining
                    productItem.organization_id ===
                      transactionData?.buyer_id?.value)
              )

              return {
                transaction_item_id: item.id,
                product_id: {
                  value: product?.id,
                  label: product?.name || '-',
                },
                product_item_id: matchedProductItem
                  ? {
                      value: matchedProductItem.id,
                      label: `${matchedProductItem.code} - ${product?.name || '-'}`,
                    }
                  : null,
                category_id: {
                  value: item.category?.id,
                  label: item.category?.name || '-',
                },
                condition_id: {
                  value: item.condition?.id,
                  label: item.condition?.name || '-',
                },
                address_id: item.address?.alias
                  ? {
                      value: item.address.id,
                      label: item.address.formatted_address,
                    }
                  : null,
                location_id: item.location?.id
                  ? {
                      value: item.location.id,
                      label: item.location.formatted_location,
                    }
                  : '-',
                warranty_date: item.warranty_date
                  ? formatDate({ rawDate: item.warranty_date, showTime: false })
                  : '-',
                unit_cost: parseFloat(item.unit_cost || 0).toFixed(2),
                total_cost: (
                  (parseFloat(item.unit_cost) || 0) * (item.quantity || 0)
                ).toFixed(2),
                quantity: item.quantity || 0,
              }
            }),
          }),
          ...(equipments.length > 0 && {
            equipments: equipments.map((transactionItem) => ({
              equipment_id: transactionItem.equipment?.number
                ? {
                    value: transactionItem.equipment.id,
                    label: `${transactionItem.equipment.number} - ${transactionItem.equipment?.name}`,
                  }
                : transactionItem.equipment?.name || '-',
              address_id: transactionItem.address?.alias
                ? {
                    value: transactionItem.address.id,
                    label: transactionItem.address.formatted_address,
                  }
                : null,
              location_id: transactionItem.location?.area
                ? {
                    value: transactionItem.location.id,
                    label: transactionItem.location.formatted_location,
                  }
                : null,
            })),
          }),
        }
      },
    }
  )

  const [items, setItems] = useState([])
  const [equipments, setEquipments] = useState([])

  useEffect(() => {
    if (
      !isLoadingTransactionData &&
      transactionData &&
      !isLoadingItemsData &&
      itemsData
    ) {
      const formData = {
        transaction: {
          transaction_type_id: transactionData.transaction_type_id,
          transaction_date: transactionData.transaction_date,
          supplier_id: transactionData.supplier_id,
          sender_id: transactionData.sender_id,
          customer_id: transactionData.customer_id,
          receiver_id: transactionData.receiver_id,
          doc_type_id: transactionData.doc_type_id,
          doc_number: transactionData.doc_number,
          buyer_id: transactionData.buyer_id,
          user_id: transactionData.user_id,
          sender_address_id: transactionData.sender_address_id, // ⬅️ Usa o nome correto
          doc_date: transactionData.doc_date,
          purchase_date: transactionData.purchase_date,
          details: transactionData.details,
          asset_type:
            itemsData.items?.length > 0
              ? [{ value: 'insumo', label: 'Insumo' }]
              : [{ value: 'equipamento', label: 'Equipamento' }],
        },
        items: itemsData.items,
        equipments: itemsData.equipments,
      }

      setFormData(formData)
      setItems(itemsData.items)
      setEquipments(itemsData.equipments)
    }
  }, [transactionData, itemsData])

  const handleItemsChange = useCallback(
    (newItems) => {
      setItems(newItems)
      setFormData((prev) => ({ ...prev, items: newItems }))
    },
    [setFormData]
  )

  const handleEquipmentsChange = useCallback(
    (newEquipments) => {
      setEquipments(newEquipments)
      setFormData((prev) => ({ ...prev, equipments: newEquipments }))
    },
    [setFormData]
  )

  // --- Hooks e buscas de dados no NÍVEL SUPERIOR ---
  const transactionTypeSelect = useRemoteSelectOptions({
    queryKey: ['almox_transactions_types'],
    fetcher: async ({ page = 1, perPage = 15, search = '' }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { name: search }),
      })

      const params = { filters, page, perPage }

      const response = await baseServiceGet(
        entities.transactionTypes.get,
        params
      )
      return response.result
    },
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (item) => ({ value: item.id, label: item.name }),
    fieldId: 'transaction.transaction_type_id',
    formData,
    setFormData,
  })

  const isEntrada =
    transactionTypeSelect.selectedValue?.value === TransactionEnums.ENTRADA
  const isTransferencia =
    transactionTypeSelect.selectedValue?.value ===
    TransactionEnums.TRANSFERENCIA

  const supplierSelect = useRemoteSelectOptions({
    queryKey: ['almox_suppliers'],
    fetcher: async ({ page = 1, perPage = 15, search = '' }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { name: search }),
      })

      const params = { filters, page, perPage }

      const response = await baseServiceGet(entities.suppliers.get, params)
      return response.result
    },
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.name}`,
    }),
    fieldId: 'transaction.supplier_id',
    formData,
    setFormData,
    refetchOnWindowFocus: true,
  })

  const docTypeSelect = useRemoteSelectOptions({
    queryKey: ['almox_documents_types'],
    fetcher: async ({ page = 1, perPage = 15, search = '' }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { name: search }),
      })

      const params = { filters, page, perPage }

      const response = await baseServiceGet(entities.documentsTypes.get, params)
      return response.result
    },
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (item) => ({ value: item.id, label: item.name }),
    fieldId: 'transaction.doc_type_id',
    formData,
    setFormData,
  })

  const { data: organizations } = useQueryWithLoader({
    queryKey: ['almox_organizations'],
    queryFn: async () =>
      baseServiceGet(entities.organizations.get, {
        deleted_at: false,
      }).then((res) => res.result.data),
  })
  const buyerSelect = useSelectOptions({
    fieldId: 'transaction.buyer_id',
    formData,
    rawOptions: organizations,
    isMulti: false,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.name}`,
    }),
    setFormData,
  })

  const senderSelect = useSelectOptions({
    fieldId: 'transaction.sender_id',
    formData,
    rawOptions: [
      TransactionEnums.TRANSFERENCIA,
      TransactionEnums.SAIDA,
    ].includes(transactionTypeSelect.selectedValue?.value)
      ? organizations?.filter((org) => org.id === selectedOrgan?.id)
      : [],
    isMulti: false,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.name}`,
    }),
    setFormData,
  })

  const senderIdRef = useRef(senderSelect.selectedValue?.value)

  const senderAddressSelect = useRemoteSelectOptions({
    queryKey: ['sender_address'],
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.formatted_address}`,
    }),
    baseServiceGet,
    columnDataBase: ['alias', 'zip', 'street', 'district'],
    entityPath: entities.addresses.get,
    extraFilters: {
      addressable_type: 'organization',
      addressable_id: senderSelect.selectedValue?.value,
    },
    defaultSort: {
      column: 'id',
      order: 'desc',
    },
    enabled: !!senderSelect.selectedValue?.value,
    fieldId: 'transaction.sender_address_id', // ⬅️ CORRIGIDO
    formData,
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    refetchOnWindowFocus: true,
    refetchWhen: () => {
      const currentSenderValue = senderSelect.selectedValue?.value

      if (senderIdRef.current !== currentSenderValue) {
        const isInitialPopulation = !senderIdRef.current && currentSenderValue

        senderIdRef.current = currentSenderValue

        if (!isInitialPopulation) {
          senderAddressSelect.refetch({ cleanedForm: true })
        } else {
          senderAddressSelect.refetch()
        }
        return true
      }
      return false
    },
  })

  const receiverSelect = useSelectOptions({
    fieldId: 'transaction.receiver_id',
    formData,
    rawOptions: organizations,
    isMulti: false,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.name}`,
    }),
    setFormData,
  })

  const customerSelect = useRemoteSelectOptions({
    queryKey: ['almox_customers'],
    fetcher: async ({ page = 1, perPage = 15, search = '' }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        alias: ['ALUCOM', 'MOREIA', 'IP', 'AS SISTEMAS'],
        ...(search && { name: search }),
      })

      const params = { filters, page, perPage }

      const response = await baseServiceGet(entities.customers.get, params)
      return response.result
    },
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.alias}`,
    }),
    fieldId: 'transaction.customer_id',
    formData,
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    refetchOnWindowFocus: true,
  })

  const buyerSelectedRef = useRef(buyerSelect.selectedValue?.value)
  const customerSelectedRef = useRef(customerSelect.selectedValue?.value)
  const receiverSelectedRef = useRef(receiverSelect.selectedValue?.value)

  const addressesSelect = useRemoteSelectOptions({
    queryKey: ['almox_addresses'],
    baseServiceGet,
    columnDataBase: ['alias', 'zip', 'street', 'district'],
    entityPath: entities.addresses.get,
    extraFilters: {
      ...(isEntrada && {
        addressable_type: 'organization',
        addressable_id: selectedOrgan?.id, // ⬅️ Corrigido para usar ID da organização logada
      }),

      ...(isTransferencia && {
        addressable_type: 'organization',
        addressable_id: organizations?.find(
          (receiver) => receiver.id === receiverSelect.selectedValue?.value
        )?.id,

        id: { $ne: senderAddressSelect.selectedValue?.value },
      }),
    },
    fieldId: 'items.address_id',
    formData: itemFormData,
    setFormData: setItemFormData,
    enabled:
      (isEntrada && selectedOrgan?.id) ||
      (isTransferencia && receiverSelect.selectedValue?.value),
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.formatted_address}`,
    }),
    initialPage: 1,
    perPage: 15,
    enablePagination: true,
    refetchOnWindowFocus: true,
    refetchWhen: () => {
      if (buyerSelectedRef.current !== buyerSelect.selectedValue?.value) {
        buyerSelectedRef.current = buyerSelect.selectedValue?.value
        return true
      }
      if (customerSelectedRef.current !== customerSelect.selectedValue?.value) {
        customerSelectedRef.current = customerSelect.selectedValue?.value
        return true
      }
      if (receiverSelectedRef.current !== receiverSelect.selectedValue?.value) {
        receiverSelectedRef.current = receiverSelect.selectedValue?.value
        return true
      }
      return false
    },
  })

  const locationsSelect = useRemoteSelectOptions({
    queryKey: ['almox_locations', addressesSelect.selectedValue?.value],
    fetcher: async ({ page = 1, perPage = 15, search = '' }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { area: search }),
      })

      const params = { filters, page, perPage }

      const response = await baseServiceGet(
        entities.addresses.locations.get(addressesSelect.selectedValue?.value),
        params
      )
      return response.result
    },
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.formatted_location}`,
    }),
    fieldId: 'items.location_id',
    formData: itemFormData,
    setFormData: setItemFormData,
    refetchOnWindowFocus: true,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    enabled: !!addressesSelect.selectedValue?.value,
  })

  const productsSelect = useRemoteSelectOptions({
    queryKey: ['almox_products'],
    baseServiceGet,
    entityPath: entities.products.get,
    columnDataBase: ['name'],
    extraFilters: {
      $or: [
        { 'items.organization_id': { $eq: selectedOrgan?.id } },
        { $doesnthave: 'items' },
      ],
    },
    defaultSort: { column: 'is_asset', order: 'desc' },
    fieldId: 'items.product_id',
    formData: itemFormData,
    isMulti: false,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.is_asset ? 'Equipamento' : 'Insumo'} - ${item.name}`,
    }),
    setFormData: setItemFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    refetchOnWindowFocus: true,
  })

  const productsItemsSelect = useRemoteSelectOptions({
    queryKey: [
      'almox_products_items',
      itemFormData.items.category_id?.value,
      itemFormData.items.condition_id?.value,
      itemFormData.items.address_id?.value,
      isTransferencia,
    ],
    baseServiceGet,
    columnDataBase: ['code', 'product.name'],
    entityPath: entities.productsItems.get,
    extraFilters: {
      ...(isTransferencia
        ? {
            category_id: { $eq: itemFormData.items.category_id?.value },
            condition_id: { $eq: itemFormData.items.condition_id?.value },
            address_id: { $eq: itemFormData.items.address_id?.value },
            organization_id: { $eq: senderSelect.selectedValue?.value },
          }
        : {}),
      quantity: { $gt: 0 },
      organization_id: { $eq: selectedOrgan?.id },
    },
    fieldId: 'items.product_item_id',
    formData: itemFormData,
    setFormData: setItemFormData,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.product?.name ?? '-'} - Quantidade: ${item.quantity ?? 0}`,
    }),
    isMulti: false,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    refetchOnWindowFocus: true,
  })

  const categoriesSelect = useRemoteSelectOptions({
    queryKey: ['almox_categories'],
    fetcher: async ({ page = 1, perPage = 15, search = '' }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { name: search }),
      })
      const params = { filters, page, perPage }
      const response = await baseServiceGet(entities.categories.get, params)
      return response.result
    },
    fieldId: 'items.category_id',
    formData: itemFormData,
    setFormData: setItemFormData,
    mapToOption: (item) => ({
      value: item.id,
      label: item.name,
    }),
    initialPage: 1,
    perPage: 15,
    enablePagination: true,
  })

  const conditionsSelect = useRemoteSelectOptions({
    queryKey: ['almox_conditions'],
    fetcher: async ({ page = 1, perPage = 15, search = '' }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { name: search }),
      })

      const params = { filters, page, perPage }

      const response = await baseServiceGet(entities.conditions.get, params)
      return response.result
    },
    fieldId: 'items.condition_id',
    formData: itemFormData,
    setFormData: setItemFormData,
    mapToOption: (item) => ({
      value: item.id,
      label: item.name,
    }),
    initialPage: 1,
    perPage: 15,
    enablePagination: true,
  })

  useEffect(() => {
    const productItem = productsItemsSelect.rawData?.find(
      (p) => p.id === Number(itemFormData.items.product_item_id?.value)
    )

    if (!productItem) return

    setItemFormData((prev) => ({
      ...prev,
      items: {
        ...prev.items,
        category_id: {
          value: productItem.category.id,
          label: productItem.category.name,
        },
        condition_id: {
          value: productItem.condition.id,
          label: productItem.condition.name,
        },
      },
    }))
  }, [itemFormData.items.product_item_id?.value, productsItemsSelect.rawData])

  const equipmentsSelect = useRemoteSelectOptions({
    queryKey: ['almox_equipments', senderSelect.selectedValue?.value],
    entityPath: entities.organizations.equipments.get(
      senderSelect.selectedValue?.value
    ),
    columnDataBase: ['number', 'name'],
    baseServiceGet,
    extraFilters: {
      status_id: { $eq: 1 },
      is_completed: { $eq: 1 },
    },
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.number} - ${item.name}`,
    }),
    fieldId: 'equipments.equipment_id',
    formData: equipmentFormData,
    setFormData: setEquipmentFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    refetchOnWindowFocus: true,
    enabled: !!senderSelect.selectedValue?.value,
  })

  const equipmentAddressesSelect = useRemoteSelectOptions({
    queryKey: ['almox_addresses_for_equipment'],
    baseServiceGet,
    columnDataBase: ['alias', 'zip', 'street', 'district'],
    extraFilters: {
      ...(isTransferencia && {
        addressable_type: 'organization',
        addressable_id: organizations?.find(
          (receiver) => receiver.id === receiverSelect.selectedValue?.value
        )?.id,

        id: { $ne: senderAddressSelect.selectedValue?.value },
      }),
    },
    entityPath: entities.addresses.get,
    fieldId: 'equipments.address_id',
    formData: equipmentFormData,
    setFormData: setEquipmentFormData,
    enabled: isTransferencia && receiverSelect.selectedValue?.value,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.formatted_address}`,
    }),
    initialPage: 1,
    perPage: 15,
    enablePagination: true,
    refetchWhen: () => {
      if (customerSelectedRef.current !== customerSelect.selectedValue?.value) {
        customerSelectedRef.current = customerSelect.selectedValue?.value
        return true
      }
      if (receiverSelectedRef.current !== receiverSelect.selectedValue?.value) {
        receiverSelectedRef.current = receiverSelect.selectedValue?.value
        return true
      }
      return false
    },
  })

  const equipmentLocationsSelect = useRemoteSelectOptions({
    queryKey: [
      'almox_equipment_locations',
      equipmentAddressesSelect.selectedValue?.value,
    ],
    fetcher: async ({ page = 1, perPage = 15, search = '' }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { area: search }),
      })

      const params = { filters, page, perPage }

      const response = await baseServiceGet(
        entities.addresses.locations.get(
          equipmentAddressesSelect.selectedValue?.value
        ),
        params
      )
      return response.result
    },
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.formatted_location}`,
    }),
    fieldId: 'equipments.location_id',
    formData: equipmentFormData,
    setFormData: setEquipmentFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    enabled: !!equipmentAddressesSelect.selectedValue?.value,
  })

  const assetTypeOptionsRaw = useMemo(
    () => [
      { value: 'equipamento', name: 'Equipamento' },
      { value: 'insumo', name: 'Insumo' },
    ],
    []
  )

  const assetTypeSelect = useSelectOptions({
    fieldId: 'transaction.asset_type',
    formData,
    rawOptions: assetTypeOptionsRaw,
    isMulti: true,
    mapToOption: (item) => ({ value: item.value, label: item.name }),
    setFormData,
  })

  const {
    transactionFieldsState,
    productsFieldsState,
    equipmentsFieldsState,
    isDisabledButton,
    setIsEquipmentSelected,
    setIsInsumoSelected,
    setTransactionTypeSelectedValue,
    setReceiverSelectedValue,
    setSenderSelectedValue,
    setIsCustomerSelected,
    setIsBuyerSelected,
  } = useTransactionPageLogic(
    items,
    equipments,
    isEntrada,
    isTransferencia,
    productsSelect.rawData,
    productsSelect,
    setFormData,
    handleItemChange,
    addressesSelect,
    locationsSelect,
    equipmentAddressesSelect,
    equipmentLocationsSelect,
    true,
    docTypeSelect
  )

  const assetTypeSelectedValue = assetTypeSelect.selectedValue

  useEffect(() => {
    setTransactionTypeSelectedValue(
      transactionTypeSelect.selectedValue?.value ?? null
    )
    setSenderSelectedValue(senderSelect.selectedValue?.value ?? null)
    setReceiverSelectedValue(receiverSelect.selectedValue?.value ?? null)
    setIsCustomerSelected(customerSelect.selectedValue?.value ?? null)
    setIsBuyerSelected(buyerSelect.selectedValue?.value ?? null)
  }, [
    transactionTypeSelect.selectedValue?.value,
    senderSelect.selectedValue?.value,
    receiverSelect.selectedValue?.value,
    customerSelect.selectedValue?.value,
    buyerSelect.selectedValue?.value,
    setTransactionTypeSelectedValue,
    setSenderSelectedValue,
    setReceiverSelectedValue,
    setIsCustomerSelected,
    setIsBuyerSelected,
  ])

  useEffect(() => {
    let hasEquipmento = false
    let hasInsumo = false

    if (isTransferencia && assetTypeSelectedValue) {
      hasEquipmento = assetTypeSelectedValue.some(
        (item) => item?.value === 'equipamento'
      )
      hasInsumo = assetTypeSelectedValue.some(
        (item) => item?.value === 'insumo'
      )
      setIsEquipmentSelected(hasEquipmento)
      setIsInsumoSelected(hasInsumo)
    }

    if (isEntrada) {
      hasInsumo = true
    }

    if (isTransferencia) {
      hasEquipmento = true
      hasInsumo = true
    }

    const showEquipmento = isTransferencia && hasEquipmento
    const showInsumo = hasInsumo

    setShowEquipmentsSection(showEquipmento)
    setShowProductsSection(showInsumo)
  }, [assetTypeSelectedValue, transactionTypeSelect.selectedValue?.value])

  const selects = {
    'transaction.transaction_type_id': transactionTypeSelect,
    'transaction.supplier_id': supplierSelect,
    'transaction.doc_type_id': docTypeSelect,
    'transaction.buyer_id': buyerSelect,
    'transaction.sender_id': senderSelect,
    'transaction.sender_address_id': senderAddressSelect,
    'transaction.receiver_id': receiverSelect,
    'transaction.asset_type': assetTypeSelect,
    'transaction.customer_id': customerSelect,
  }

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      if (!selectConfig) return {}

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [selects]
  )

  const getOptions = useCallback(
    (fieldId) => selects[fieldId]?.options || [],
    [selects]
  )
  const getSelectedValue = useCallback(
    (fieldId) => selects[fieldId]?.selectedValue || null,
    [selects]
  )

  const itemSelects = {
    'items.product_id': productsSelect,
    'items.product_item_id': productsItemsSelect,
    'items.category_id': categoriesSelect,
    'items.condition_id': conditionsSelect,
    'items.address_id': addressesSelect,
    'items.location_id': locationsSelect,
  }

  const getSelectMetaItem = useCallback(
    (fieldId) => {
      const selectConfig = itemSelects[fieldId]
      if (!selectConfig) return {}

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [itemSelects]
  )

  const getItemOptions = useCallback(
    (fieldId) => itemSelects[fieldId]?.options || [],
    [itemSelects]
  )
  const getItemSelectedValue = useCallback(
    (fieldId) => itemSelects[fieldId]?.selectedValue || null,
    [itemSelects]
  )

  const equipmentSelects = {
    'equipments.equipment_id': equipmentsSelect,
    'equipments.address_id': equipmentAddressesSelect,
    'equipments.location_id': equipmentLocationsSelect,
  }

  const getSelectMetaEquipment = useCallback(
    (fieldId) => {
      const selectConfig = equipmentSelects[fieldId]
      if (!selectConfig) return {}

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [equipmentSelects]
  )

  const getEquipmentItemOptions = useCallback(
    (fieldId) => equipmentSelects[fieldId]?.options || [],
    [equipmentSelects]
  )
  const getEquipmentItemSelectedValue = useCallback(
    (fieldId) => equipmentSelects[fieldId]?.selectedValue || null,
    [equipmentSelects]
  )

  const handleSaveItem = useCallback(
    (itemToEdit) => {
      let updatedItems
      const itemData = {
        ...convertStringsToFloats(itemFormData.items),
        warranty_date: itemFormData.items.warranty_date,
      }

      const quantity = itemData.quantity
      if (quantity === null || quantity === undefined || quantity < 1) {
        showNotification('warning', 'O campo Quantidade deve ser maior que 0.')
        return false
      }

      const ignoreMap = {
        entrada: ['items.product_item_id'],
        transferencia: [
          'items.product_id',
          'items.warranty_date',
          'items.unit_cost',
        ],
      }

      const missingFields = getMissingRequiredFields({
        itemData,
        fields: productsSection.fields,
        prefix: 'items',
        ignore: isEntrada
          ? ignoreMap.entrada
          : isTransferencia
            ? ignoreMap.transferencia
            : [],
      })

      if (missingFields.length > 0) {
        showNotification('warning', 'Preencha todos os campos obrigatórios')
        return false
      }

      if (itemToEdit) {
        updatedItems = items.map((item) =>
          item.id === itemToEdit.id ? { ...item, ...itemData } : item
        )
      } else {
        updatedItems = [...items, { id: uuidv4().slice(0, 8), ...itemData }]
      }

      setItems(updatedItems)
      handleItemsChange(updatedItems)
      return true
    },
    [items, itemFormData, handleItemsChange]
  )

  const initializeItemForm = useCallback(
    (itemData) => {
      setItemFormData({ items: itemData || {} })
    },
    [setItemFormData]
  )

  const handleSaveEquipment = useCallback(
    (equipmentToEdit) => {
      let updatedEquipments
      const equipmentData = convertStringsToFloats(equipmentFormData.equipments)

      const missingFields = getMissingRequiredFields({
        itemData: equipmentData,
        fields: equipmentsSection.fields,
        prefix: 'equipments',
        ignore: [],
      })

      if (missingFields.length > 0) {
        showNotification(
          'warning',
          'Preencha todos os campos obrigatórios para equipamento'
        )
        return false
      }

      if (equipmentToEdit) {
        updatedEquipments = equipments.map((equipment) =>
          equipment.id === equipmentToEdit.id
            ? { ...equipment, ...equipmentData }
            : equipment
        )
      } else {
        updatedEquipments = [
          ...equipments,
          { id: uuidv4().slice(0, 8), ...equipmentData },
        ]
      }

      setEquipments(updatedEquipments)
      handleEquipmentsChange(updatedEquipments)
      return true
    },
    [equipments, equipmentFormData, handleEquipmentsChange]
  )

  const initializeEquipmentForm = useCallback(
    (equipmentData) => {
      setEquipmentFormData({ equipments: equipmentData || {} })
    },
    [setEquipmentFormData]
  )

  const updateTransactionMutation = useMutation({
    mutationFn: async (payload) => {
      showLoader()
      const response = await baseServicePut(
        entities.transactions.update(id),
        payload
      )
      return response
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        hideLoader()
        resetForm()
        resetItemForm()
        resetEquipmentForm()
        navigate(
          `/almoxarifado/transacoes/${
            isEntrada
              ? 'entrada'
              : isTransferencia
                ? 'transferencia'
                : 'uso-interno'
          }/`
        )
        queryClient.invalidateQueries(['almox_transactions'])
      }
      hideLoader()
    },
    onError: (error) => {
      console.error('onError ', error)

      forEach(error?.data, (error) => {
        showNotification('warning', error)
      })

      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors)
      }
      hideLoader()
    },
  })
  const calculateItemsTotal = (items) => {
    return items.reduce((total, item) => {
      const unitCost = parseFloat(item.unit_cost) || 0
      const quantity = parseInt(item.quantity) || 0
      return total + unitCost * quantity
    }, 0)
  }
  const handleSubmit = async () => {
    try {
      const cleanedFormData = cleanPayload(formData)

      const cleanedPayload = {
        ...cleanedFormData,
        ...(cleanedFormData.items &&
          cleanedFormData.items.length > 0 && {
            items: cleanedFormData.items.map((item) => cleanPayload(item)),
          }),
        ...(cleanedFormData.equipments &&
          cleanedFormData.equipments.length > 0 && {
            equipments: (cleanedFormData.equipments || []).map((equipment) => {
              const clean = cleanPayload(equipment)

              const raw = equipmentsSelect.rawData?.find(
                (e) => e.id === Number(clean.equipment_id)
              )

              return {
                ...clean,
                product_item_id: raw?.product_item_id ?? null,
                product_id: raw?.product_item?.product_id ?? null,
              }
            }),
          }),
      }
      // ⬅️ VALIDAÇÃO ADICIONADA
      if (
        transactionTypeSelect.selectedValue?.value ===
          TransactionEnums.ENTRADA &&
        docTypeSelect.selectedValue?.value === DocumentTypeEnums['NOTA FISCAL']
      ) {
        const itemsTotal = calculateItemsTotal(cleanedPayload.items || [])
        const informedTotal =
          parseFloat(cleanedPayload.transaction?.amount_total) || 0

        if (Math.abs(itemsTotal - informedTotal) > 0.01) {
          showNotification(
            'error',
            `Divergência: a soma dos itens (R$ ${itemsTotal.toFixed(2)}) não confere com o valor total informado (R$ ${informedTotal.toFixed(2)}). Ajuste os valores antes de salvar.`
          )
          return
        }
      }
      await updateTransactionMutation.mutateAsync(cleanedPayload)
    } catch (error) {
      console.error('Erro ao enviar transação:', {
        status: error.status,
        message: error.message,
      })
    }
  }
  const handleBack = () =>
    navigate(
      `/almoxarifado/transacoes/${
        isEntrada
          ? 'entrada'
          : isTransferencia
            ? 'transferencia'
            : 'uso-interno'
      }/`
    )

  const renderFormBody = useCallback(
    ({ fields, formData, handleFieldChange, formErrors }) => {
      return (
        <SimpleBody
          fields={fields}
          formErrors={normalizeFormErrors(formErrors)}
          formData={formData}
          handleFieldChange={handleFieldChange}
          getOptions={getItemOptions}
          getSelectedValue={getItemSelectedValue}
          getSelectMeta={getSelectMetaItem}
        />
      )
    },
    [formErrors, getItemOptions, getItemSelectedValue]
  )

  const renderEquipmentFormBody = useCallback(
    ({ fields, formData, handleFieldChange, formErrors }) => {
      return (
        <SimpleBody
          fields={fields}
          formErrors={normalizeFormErrors(formErrors)}
          formData={formData}
          handleFieldChange={handleFieldChange}
          getOptions={getEquipmentItemOptions}
          getSelectedValue={getEquipmentItemSelectedValue}
          getSelectMeta={getSelectMetaEquipment}
        />
      )
    },
    [formErrors, getEquipmentItemOptions, getEquipmentItemSelectedValue]
  )

  const getItemDisplayValue = useCallback((item, fieldId) => {
    const fieldName = fieldId.split('.').pop()
    const value = item[fieldName]

    if (value && typeof value === 'object' && 'label' in value) {
      return value.label
    }

    return value || ''
  }, [])

  const getEquipmentDisplayValue = getItemDisplayValue

  return (
    <MainLayout>
      <PageHeader
        title="Edição de Transação"
        showBackButton={true}
        backUrl={`/almoxarifado/transacoes/${
          isEntrada ? 'entrada' : isTransferencia ? 'transferencia' : 'entrada'
        }/`}
      />
      <div className="container-fluid p-1">
        <Form
          initialFormData={{ ...formData, items: items }}
          onSubmit={handleSubmit}
          textSubmit="Atualizar Transação"
          textLoadingSubmit="Atualizando..."
          handleBack={handleBack}
          isLoading={false}>
          {() => (
            <>
              <div key={transactionSection.section}>
                <SectionHeader section={transactionSection.section} />
                <SimpleBody
                  fields={transactionFieldsState}
                  formErrors={normalizeFormErrors(formErrors)}
                  formData={formData}
                  handleFieldChange={handleChange}
                  getOptions={getOptions}
                  getSelectedValue={getSelectedValue}
                  getSelectMeta={getSelectMeta}
                />
              </div>
              {showProductsSection && (
                <Box
                  key={productsSection.section}
                  sx={{ mt: '1rem', display: 'flex', flexDirection: 'column' }}>
                  <ItemListSection
                    section={productsSection.section}
                    fields={productsFieldsState}
                    initialData={items}
                    onItemsChange={handleItemsChange}
                    renderFormBody={renderFormBody}
                    getItemDisplayValue={getItemDisplayValue}
                    itemFormData={itemFormData}
                    onItemChange={handleItemChange}
                    itemFormErrors={formErrors}
                    onFormSubmit={handleSaveItem}
                    initializeItemForm={initializeItemForm}
                    isDisabledButton={isDisabledButton.insumo}
                  />
                </Box>
              )}
              {showEquipmentsSection && (
                <Box
                  key={equipmentsSection.section}
                  sx={{ mt: '1rem', display: 'flex', flexDirection: 'column' }}>
                  <ItemListSection
                    section={equipmentsSection.section}
                    fields={equipmentsFieldsState}
                    initialData={equipments}
                    onItemsChange={handleEquipmentsChange}
                    renderFormBody={renderEquipmentFormBody}
                    getItemDisplayValue={getEquipmentDisplayValue}
                    itemFormData={equipmentFormData}
                    onItemChange={handleEquipmentChange}
                    itemFormErrors={formErrors}
                    onFormSubmit={handleSaveEquipment}
                    initializeItemForm={initializeEquipmentForm}
                    isDisabledButton={isDisabledButton.equipamento}
                  />
                </Box>
              )}
            </>
          )}
        </Form>
      </div>
    </MainLayout>
  )
}

export default EditTransactionPage
